import { useAuth0 } from '@auth0/auth0-react'
import { Alert, ButtonLink } from '@designsforhealth/dfh-react-components'
import { Button } from '@dfh-corp-sites/ui'
import { XIcon } from '@dfh-corp-sites/ui/icons'
import { CircularProgress } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import React, { useContext, useReducer } from 'react'
import styled from 'styled-components'

import { CartFragment } from '../../../graphql/magento'
import { useCustomerId } from '../../../hooks/useCustomerId'
import { useResizeObserver } from '../../../hooks/useResizeObserver'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import { publishBeginCheckoutEvent } from '../../../lib/analytics/cart'
import { isMagentoScopeNotGrantedError } from '../../../lib/auth/errors'
import { useCartContext } from '../../../lib/cart'
import { ToggleCartContext } from '../../../lib/cart/context'
import { setCartFooterHeightFromBoxSize } from '../../../lib/cart/footer'
import {
  collectItemAndDesignFeePairs,
  totalQuantityExcludingDependents,
} from '../../../lib/cart/item'
import { CartStateReduceAction, reduceCartState } from '../../../lib/cart/reducers/cart-state'
import { publishConversionsBeginCheckout } from '../../../lib/conversions/actions'
import { usePageUrl } from '../../../lib/pages'
import { pluralize } from '../../../utils/textTools'
import CartItemContainer from '../../cart/CartItemContainer'
import { CartItemErrors } from '../../cart/CartItemErrors'
import CartPromoCode from '../../cart/CartPromoCode'
import CartSummary from '../../cart/CartSummary'
import ErrorAccessingMagentoMessage from '../../ErrorAccessingMagentoMessage'
import CartDetailsContainer from './CartDetailsContainer'
import CartHeader from './CartHeader'
import CartItem from './CartItem'
import CartModal from './CartModal'
import LoginToViewCart from './LoginToViewCart'

import closeModalIcon from '../../../img/close_modal.svg'

const CartItems: React.FC<{
  cartId: CartFragment['id']
  dispatch: React.Dispatch<CartStateReduceAction>
  items: CartFragment['items']
  shippingAddress: NonNullable<CartFragment['shipping_addresses']>[number] | undefined
}> = ({ cartId, dispatch, items, shippingAddress }) => {
  const { featureFlags } = useSiteMetadata() ?? {}

  // if label status (PL) feature flag enabled, pair items with design fee items efficiently
  if (featureFlags?.productLabelStatus) {
    const elements: React.ReactElement[] = []
    for (const pair of collectItemAndDesignFeePairs(items ?? []).values()) {
      elements.push(
        <CartItemContainer
          cartId={cartId}
          component={CartItem}
          item={pair.item}
          labelDesignFeeItem={pair.designFeeItem}
          key={pair.item.id}
          requestDispatch={dispatch}
          shippingAddress={shippingAddress}
        />,
      )
    }
    return <>{elements}</>
  }

  const elements = (items ?? []).reduce((acc: React.ReactElement[], item) => {
    if (!item) return acc
    acc.push(
      <CartItemContainer
        cartId={cartId}
        component={CartItem}
        item={item}
        key={item.id}
        requestDispatch={dispatch}
        shippingAddress={shippingAddress}
      />,
    )
    return acc
  }, [])

  return <>{elements}</>
}

const StyledCartItemErrors = styled(CartItemErrors)`
  margin-bottom: 0;
  ${Alert} {
    margin-top: 5px;
    margin-left: 5px;
    margin-right: 5px;
  }
`

const CartDetails: React.FC<{
  cartId: CartFragment['id']
  dispatch: React.Dispatch<CartStateReduceAction>
  items: CartFragment['items']
  shippingAddress: NonNullable<CartFragment['shipping_addresses']>[number] | undefined
  toggleCartModal: () => void
  totalQuantity: CartFragment['total_quantity']
  subtotalWithDiscountExclTax: NonNullable<
    NonNullable<CartFragment['prices']>['subtotal_with_discount_excluding_tax']
  >['value']
}> = React.memo(({ cartId, dispatch, items, shippingAddress, toggleCartModal, totalQuantity }) => (
  <div className="flex flex-col flex-1 overflow-hidden">
    <header className="flex justify-between p-6 bg-brand-grey border-b border-b-brand-rule-grey flex-shrink-0">
      <p>
        <strong className="text-[32px]">Your Cart</strong>
        <strong className="text-lg text-brand-navy-2 ml-5 md:ml-10">
          {totalQuantity} {pluralize({ count: totalQuantity, word: 'item' })}{' '}
        </strong>
      </p>
      <ButtonLink
        onClick={toggleCartModal}
        underline={false}
        className="self-start"
        aria-label="Close cart"
      >
        <XIcon />
      </ButtonLink>
    </header>
    <main
      id="cart-items-container"
      className="flex px-2.5 py-5 flex-col bg-brand-grey overflow-y-auto flex-1"
    >
      <Subscribe>
        <StyledCartItemErrors />
        {/* {items?.length ? (
            <CartUpsellFreeShipping
              cartPriceTotal={subtotalWithDiscountExclTax}
              shippingAddress={shippingAddress}
            />
          ) : null} */}
        {items?.length ? (
          <CartItems
            cartId={cartId}
            dispatch={dispatch}
            items={items}
            shippingAddress={shippingAddress}
          />
        ) : (
          <h3>Your cart is empty</h3>
        )}
      </Subscribe>
    </main>
  </div>
))

CartDetails.displayName = 'CartDetails'

const Cart: React.FC = () => {
  const { navigate } = usePageUrl()
  const { cart, error, loading } = useCartContext()
  const { featureFlags } = useSiteMetadata() ?? {}
  const { isCartOpen, toggleCart: toggleCartModal } = useContext(ToggleCartContext)
  const authState = useAuth0()
  const customerId = useCustomerId()
  const isProvisionalUser = authState.isAuthenticated && !customerId

  const goToShipping = () => {
    if (!cart || authState.isLoading) {
      return
    }

    publishBeginCheckoutEvent(cart)
    publishConversionsBeginCheckout(cart)

    if (!authState.isAuthenticated) {
      navigate('/checkout/verify/')
      return
    }

    if (isProvisionalUser) {
      navigate('/checkout/shipping/')
      return
    }

    navigate('/checkout/review/')
  }
  const [{ codesCollapsed, formDisabled }, dispatch] = useReducer(reduceCartState, {
    codesCollapsed: true,
    formDisabled: false,
    requestsPending: [],
    summaryVisible: true,
  })

  const resizeObserverRef = useResizeObserver(setCartFooterHeightFromBoxSize)

  // Check if anonymous cart is enabled
  const anonymousCartEnabled = featureFlags?.anonymousCart ?? false

  if (isMagentoScopeNotGrantedError(error)) {
    return (
      <CartModal
        allowScroll
        isOpen={isCartOpen}
        onBackgroundClick={toggleCartModal}
        onEscapeKeydown={toggleCartModal}
      >
        <CartDetailsContainer>
          <CartHeader>
            <strong>YOUR CART:</strong>
            <ButtonLink onClick={toggleCartModal} underline={false}>
              <img alt="Close cart modal" src={closeModalIcon} />
            </ButtonLink>
          </CartHeader>
          <Alert type="error">
            <ErrorAccessingMagentoMessage />
          </Alert>
        </CartDetailsContainer>
      </CartModal>
    )
  }

  if (!isCartOpen || (!anonymousCartEnabled && authState.isLoading)) {
    return null
  }

  // Show login prompt only if user is not authenticated AND anonymous cart is disabled
  if (!cart && !anonymousCartEnabled && !authState.isLoading && !authState.isAuthenticated) {
    return <LoginToViewCart toggleCartModal={toggleCartModal} isCartOpen={isCartOpen} />
  }

  // For guest users with anonymous cart enabled, show empty cart or loading state
  if (!cart) {
    return (
      <CartModal
        allowScroll
        isOpen={isCartOpen}
        onBackgroundClick={toggleCartModal}
        onEscapeKeydown={toggleCartModal}
      >
        <CartDetailsContainer className="flex flex-col h-full">
          <div className="flex flex-col flex-1 overflow-hidden">
            <header className="flex justify-between p-6 bg-brand-grey border-b border-b-brand-rule-grey flex-shrink-0">
              <p>
                <strong className="text-[32px]">Your Cart</strong>
                <strong className="text-lg text-brand-navy-2 ml-5 md:ml-10">0 items</strong>
              </p>
              <ButtonLink
                onClick={toggleCartModal}
                underline={false}
                className="self-start"
                aria-label="Close cart"
              >
                <XIcon />
              </ButtonLink>
            </header>
            <main
              id="cart-items-container"
              className="flex px-2.5 py-5 flex-col bg-brand-grey overflow-y-auto flex-1"
            >
              {loading ? (
                <div className="flex justify-center items-center p-8">
                  <div>Loading your cart...</div>
                </div>
              ) : (
                <h3>Your cart is empty</h3>
              )}
            </main>
          </div>
          <footer className="flex flex-col w-full bg-brand-grey mt-auto" ref={resizeObserverRef}>
            <div className="px-5 py-3.5 flex flex-col gap-3">
              <div className="text-center text-sm text-gray-600">
                Add items to your cart to continue
              </div>
              <Button
                size={'md'}
                variant={'default'}
                onClick={toggleCartModal}
                disabled={true}
                data-cy="minicart-secure-checkout-button"
                className="m-auto self-center"
              >
                Secure Checkout
              </Button>
            </div>
          </footer>
        </CartDetailsContainer>
      </CartModal>
    )
  }

  const {
    id: cartId,
    items,
    shipping_addresses: shippingAddresses,
    total_quantity: totalQuantity,
    prices,
  } = cart

  const [shippingAddress] = shippingAddresses || []
  const cartTotalQuantity = totalQuantityExcludingDependents(items, totalQuantity)

  return (
    <CartModal
      allowScroll
      isOpen={isCartOpen}
      onBackgroundClick={toggleCartModal}
      onEscapeKeydown={toggleCartModal}
    >
      <CartDetailsContainer className="flex flex-col h-full">
        <CartDetails
          cartId={cartId}
          dispatch={dispatch}
          items={items}
          shippingAddress={shippingAddress}
          toggleCartModal={toggleCartModal}
          totalQuantity={cartTotalQuantity}
          subtotalWithDiscountExclTax={prices?.subtotal_with_discount_excluding_tax?.value}
        />
        <footer className="flex flex-col w-full bg-brand-grey mt-auto" ref={resizeObserverRef}>
          <CartPromoCode
            cart={cart}
            codesCollapsed={codesCollapsed}
            dispatch={dispatch}
            formDisabled={formDisabled}
          />
          <div className="px-5 py-3.5 flex flex-col gap-3">
            <CartSummary cart={cart} summaryVisible={true} />

            <Button
              size={'md'}
              variant={'default'}
              onClick={goToShipping}
              disabled={formDisabled || !items?.length || authState.isLoading}
              data-cy="minicart-secure-checkout-button"
              className="m-auto self-center"
            >
              {authState.isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Secure Checkout'
              )}
            </Button>
          </div>
        </footer>
      </CartDetailsContainer>
    </CartModal>
  )
}

export default Cart
